import * as React from 'react';
import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { graphql } from 'react-apollo';

import type { ApolloError } from 'apollo-client';

import { getUpcomingCourseStates, getUpcomingCourseStatesOptions } from './UpcomingCourses/UpcomingCoursesQueries';
import type {
  GetUpcomingCourseStatesQuery,
  GetUpcomingCourseStatesQueryVariables,
  GetUpcomingCourseStatesQuery_DegreeLearnerCourseStatesV1Resource_get_inProgress as InProgressCourse,
  GetUpcomingCourseStatesQuery_DegreeLearnerCourseStatesV1Resource_get_notStarted as NotStartedCourse,
  GetUpcomingCourseStatesQuery_DegreeLearnerCourseStatesV1Resource_get_passed as PassedCourse,
} from './UpcomingCourses/__generated__/getUpcomingCourseStatesQuery';

type UpcomingCoursesFromGraphQLProps = {
  upcomingCoursesData?: NotStartedCourse[];
  passedCoursesData?: PassedCourse[];
  inProgressCoursesData?: InProgressCourse[];
  coursesLoading: boolean;
  coursesError?: ApolloError;
  refetchCourses?: () => {};
};

type InputProps = {
  degreeId: string;
};

type Props = InputProps & UpcomingCoursesFromGraphQLProps;

type ContextProps = UpcomingCoursesFromGraphQLProps & {
  showEnrollmentDialog: boolean;
  setShowEnrollmentDialog: Dispatch<SetStateAction<boolean>>;
};

export const CoursesContext = React.createContext<ContextProps>({
  coursesLoading: false,
  showEnrollmentDialog: false,
  setShowEnrollmentDialog: () => null,
});

export const CoursesContextProvider: React.LegacyFunctionComponentWithChildren<Props> = ({
  children,
  upcomingCoursesData,
  passedCoursesData,
  inProgressCoursesData,
  coursesLoading,
  coursesError,
  refetchCourses,
}) => {
  const [showEnrollmentDialog, setShowEnrollmentDialog] = useState(false);
  return (
    <CoursesContext.Provider
      value={{
        upcomingCoursesData,
        passedCoursesData,
        inProgressCoursesData,
        coursesLoading,
        coursesError,
        refetchCourses,
        showEnrollmentDialog,
        setShowEnrollmentDialog,
      }}
    >
      {children}
    </CoursesContext.Provider>
  );
};

export const useCoursesContext = () => React.useContext(CoursesContext);

export default graphql<
  React.PropsWithChildren<InputProps>,
  GetUpcomingCourseStatesQuery,
  GetUpcomingCourseStatesQueryVariables,
  UpcomingCoursesFromGraphQLProps
>(
  getUpcomingCourseStates,
  getUpcomingCourseStatesOptions
)(CoursesContextProvider);
